import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  ViewChild,
  inject
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { ACLService } from '@delon/acl';
import { DA_SERVICE_TOKEN, ITokenService } from '@delon/auth';
import { SettingsService, User } from '@delon/theme';
import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { PhEisMenuModeService } from 'src/app/core/services/ph-eis-menu-mode.service';
import { AuthService } from 'src/app/core/util/auth.service';
import { AppService } from 'src/app/shared/services/app.service';
import { SharedModule } from 'src/app/shared/shared.module';

import { HeaderFullScreenComponent } from './fullscreen.component';
import { MobileAppQrCodeComponent } from './mobile-app-qr-code/mobile-app-qr-code.component';

@Component({
  selector: 'header-user',
  standalone: true,
  imports: [
    NzAvatarModule,
    SharedModule,
    RouterModule,
    MobileAppQrCodeComponent,
    HeaderFullScreenComponent
  ],
  templateUrl: './user.component.html',
  styleUrls: ['./user.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeaderUserComponent {
  /** PH BIR e-Invoice menu switch. `available` is true only when `ph_eis.enabled`. */
  readonly menuMode = inject(PhEisMenuModeService);
  private readonly modalSrv = inject(NzModalService);
  private readonly appSrv = inject(AppService);

  @ViewChild('mobileAppQrCodeComponent')
  mobileAppQrCode!: MobileAppQrCodeComponent;

  get user(): User {
    return this.settings.user;
  }
  get country() {
    return this.settings.user['company']?.country;
  }
  get academyLink() {
    if (this.country === 'PH') return 'https://academy.qne.cloud';

    return 'https://academy.qne.cloud/courses/qcamy';
  }
  get websiteLink() {
    if (this.country === 'PH') return 'https://qne.cloud/ph/';

    return 'https://qne.cloud/my/';
  }
  get youtubeLink() {
    if (this.country === 'PH') return 'https://www.youtube.com/@QNESoftwarePH';

    return 'https://www.youtube.com/@QnESoftwareChannel';
  }
  get releaseNotesLink() {
    if (this.country === 'PH') {
      return 'https://support-ph.qne.cloud/support/solutions/folders/35000252635';
    }
    return 'https://support.qne.com.my/support/solutions/folders/81000302605';
  }
  get company() {
    return this.settings.user['company'];
  }

  constructor(
    private readonly settings: SettingsService,
    private readonly authService: AuthService,
    private readonly router: Router,
    public acl: ACLService,
    @Inject(DA_SERVICE_TOKEN) private readonly tokenService: ITokenService
  ) {
    // obsolete: retrieve release notes by country
    // this.http
    //   .get(
    //     `api/releaseNotes/getByCountry?country=${this.country}&_silent=true`,
    //     {},
    //     { context: new HttpContext().set(ALLOW_ANONYMOUS, true) }
    //   )
    //   .subscribe((res: any[]) => {
    //     this.releaseNotes = [...res];
    //   });
  }

  logout(): void {
    this.authService.logout();
  }

  /** Label for the quiet avatar row. Names the menu the user would switch to. */
  get menuSwitchLabel(): string {
    return this.menuMode.slim()
      ? 'Switch to Full Accounting'
      : 'Switch to e-Invoice';
  }

  /**
   * Confirm in-app before storing the other mode. Cancel leaves sessionStorage alone.
   * Confirm mutes the browser leave prompt for this reload only.
   */
  switchMenuMode(): void {
    if (!this.menuMode.available()) return;

    const toFull = this.menuMode.slim();
    const title = `${this.menuSwitchLabel}?`;
    const reloadNote = toFull
      ? 'The page will reload to switch to full accounting.'
      : 'The page will reload to switch to e-Invoice.';
    const ref = this.modalSrv.confirm({
      nzTitle: title,
      nzContent: reloadNote,
      nzOkText: 'Switch',
      nzCancelText: 'Cancel',
      nzClassName: 'header-user-menu-switch-confirm',
      nzAutofocus: 'cancel',
      nzOnOk: () => this.confirmMenuSwitch(toFull)
    });
    this.markMenuSwitchDialog(ref, title);
    ref.afterOpen.subscribe(() => this.markMenuSwitchDialog(ref, title));
  }

  private confirmMenuSwitch(toFull: boolean): void {
    this.menuMode.select(toFull ? 'full' : 'slim', () =>
      this.reloadWithoutLeavePrompt()
    );
  }

  /** `window.app.forcereload` is the flag existing beforeunload handlers already check. */
  private reloadWithoutLeavePrompt(): void {
    this.appSrv.muteLeaveAppAlert();
    window.onbeforeunload = null;
    window.addEventListener(
      'beforeunload',
      (event: BeforeUnloadEvent) => {
        event.stopImmediatePropagation();
      },
      { capture: true, once: true }
    );
    window.location.reload();
  }

  private markMenuSwitchDialog(ref: NzModalRef, title: string): void {
    const dialog = ref.getElement();
    dialog.setAttribute('aria-modal', 'true');
    const titleEl = dialog.querySelector('.ant-modal-confirm-title');
    if (titleEl instanceof HTMLElement) {
      titleEl.id = 'ph-eis-menu-switch-title';
      dialog.setAttribute('aria-labelledby', titleEl.id);
    } else {
      dialog.setAttribute('aria-label', title);
    }
    const content = dialog.querySelector('.ant-modal-confirm-content');
    if (content instanceof HTMLElement) {
      content.id = 'ph-eis-menu-switch-note';
      dialog.setAttribute('aria-describedby', content.id);
    }
    dialog.querySelectorAll('button').forEach(button => {
      button.type = 'button';
    });
  }

  showManagePlan() {
    const tokenModel = this.tokenService.get();

    if (tokenModel) {
      const currentTenantId = tokenModel['tenantId'];

      this.router.navigate(['./account/subscription'], {
        queryParams: { tid: currentTenantId }
      });
    } else {
      // Handle the case when tokenModel is null
      // You can display an error message or take appropriate action
    }
  }
}
